import React, { useEffect, useState } from "react";
import { ReactDialogBox } from "react-js-dialog-box";
import { LngLat } from "mapbox-gl";

import styles from "./Centerpanel.module.css";
import { Mappanel } from "./Mappanel/Mappanel";
import { Treepanel } from "./Treepanel/Treepanel";
import { Proppanel } from "./Proppanel/Proppanel";
import "react-js-dialog-box/dist/index.css";
import { RouteAction, RouteActionKind, ToolType } from "../../Reducer";
import {
  BackgroundSound,
  FocusedSound,
  POI,
  Point,
  RouteState,
} from "../../api/edits/editRoute.responses";
import { FindClosestSegmentWithDistance } from "./Mappanel/Mappanel.part";

//lng,lat
const DEFAULT_CENTER: [number, number] = [26.4506, 53.5125];
const startLocationOffset = 0.0005;

interface CenterpanelProps {
  currentTool: ToolType;
  setCurrentTool: React.Dispatch<React.SetStateAction<ToolType>>;
  routeState: RouteState;
  routeDispatch: React.Dispatch<RouteAction>;
  currentLanguage: string;
  stateSelected: string;
  setSelected: React.Dispatch<React.SetStateAction<string>>;
}

export const Centerpanel = (props: CenterpanelProps): JSX.Element => {
  // state and functions for DELETE dialog box
  const [stateIsDialogBoxOpenDelete, setOpenDelete] = useState(false);

  //Defining the starting location for the map and walker location
  //Its the first point moved to northwest by 10 meters to prevent immediate trigger of poi
  const [startLocation, setStartLocation] =
    useState<[number, number]>(DEFAULT_CENTER);

  useEffect(() => {
    if (props.routeState.pointsOfInterest.length !== 0)
      setStartLocation([
        props.routeState.pointsOfInterest[0].longitude + startLocationOffset,
        props.routeState.pointsOfInterest[0].latitude + startLocationOffset,
      ]);
  }, [props.routeState.pointsOfInterest[0]]);

  // pre:   called when adding feature
  //        lngLat is feature coordinate
  //        shiftKey: true when Shift was held during the click (insert between segments)
  // post:  feature is added using routeDispatch
  function createNewFeature(lngLat: LngLat, shiftKey: boolean = false): void {
    if (props.stateSelected === "fss") {
      props.routeDispatch({
        type: RouteActionKind.ADD_FOCUSED,
        payload: {
          pnt: { latitude: lngLat.lat, longitude: lngLat.lng },
        },
      });
    } else if (props.stateSelected === "bss") {
      props.routeDispatch({
        type: RouteActionKind.ADD_BACKGROUND,
        payload: {
          pnt: { latitude: lngLat.lat, longitude: lngLat.lng },
        },
      });
    } else if (props.stateSelected === "pois") {
      props.routeDispatch({
        type: RouteActionKind.ADD_POI,
        payload: {
          pnt: { latitude: lngLat.lat, longitude: lngLat.lng },
        },
      });
    } else if (props.stateSelected === "rline") {
      const clickPnt: Point = { latitude: lngLat.lat, longitude: lngLat.lng };
      if (props.routeState.linePoints.length === 0) {
        // First click: create the initial single vertex
        props.routeDispatch({
          type: RouteActionKind.ADD_ROUTELINE,
          payload: { pnt: clickPnt },
        });
      } else if (props.routeState.linePoints.length === 1) {
        // Second click: always append to form the first visible segment
        props.routeDispatch({
          type: RouteActionKind.APPEND_ROUTELINEPOINT,
          payload: { pnt: clickPnt },
        });
      } else if (shiftKey) {
        // Shift+click: insert between the vertices of the closest segment
        const { insertIndex } = FindClosestSegmentWithDistance(
          props.routeState,
          clickPnt
        );
        props.routeDispatch({
          type: RouteActionKind.ADD_ROUTELINEPOINT,
          payload: { pnt: clickPnt, insertIndex },
        });
      } else {
        // Regular click: prepend or append based on which endpoint is closer
        const first = props.routeState.linePoints[0];
        const last =
          props.routeState.linePoints[props.routeState.linePoints.length - 1];
        const distToFirst =
          (clickPnt.latitude - first.latitude) ** 2 +
          (clickPnt.longitude - first.longitude) ** 2;
        const distToLast =
          (clickPnt.latitude - last.latitude) ** 2 +
          (clickPnt.longitude - last.longitude) ** 2;

        if (distToFirst < distToLast) {
          props.routeDispatch({
            type: RouteActionKind.ADD_ROUTELINEPOINT,
            payload: { pnt: clickPnt, insertIndex: 0 },
          });
        } else {
          props.routeDispatch({
            type: RouteActionKind.APPEND_ROUTELINEPOINT,
            payload: { pnt: clickPnt },
          });
        }
      }
    } else {
      alert("Please select feature category before creating feature!");
    }
  }

  // pre:   called when deleting feature
  //        feature to delete is in stateSelected
  // post:  dialog box is shown on screen
  function openDialogBoxDelete(): void {
    setOpenDelete(true);
  }

  // pre:   called to close dialog box for deleting features
  // post:  dialog box is removed from screen
  function closeDialogBoxDelete(): void {
    setOpenDelete(false);
  }

  // pre:   called when clicked on ok
  //        feature to delete is in stateSelected
  // post:  dialog box is removed from screen
  //        feature in stateSelected is deleted using routeDispatch
  function handleDialogSubmitDelete(e: any): void {
    e.preventDefault();

    if (props.stateSelected.substring(0, 3) === "fss") {
      if (props.stateSelected.length > 3) {
        let str: string = props.stateSelected.substring(3);
        props.routeDispatch({
          type: RouteActionKind.DELETE_FOCUSED,
          payload: { id: Number(str) },
        });
      }
    } else if (props.stateSelected.substring(0, 3) === "bss") {
      if (props.stateSelected.length > 3) {
        let str: string = props.stateSelected.substring(3);
        props.routeDispatch({
          type: RouteActionKind.DELETE_BACKGROUND,
          payload: { id: Number(str) },
        });
      }
    } else if (props.stateSelected.substring(0, 4) === "pois") {
      if (props.stateSelected.length > 4) {
        let str: string = props.stateSelected.substring(4);
        props.routeDispatch({
          type: RouteActionKind.DELETE_POI,
          payload: { id: Number(str) },
        });
      }
    } else if (props.stateSelected.substring(0, 5) === "rline") {
      if (props.stateSelected.length === 5) {
        props.routeDispatch({
          type: RouteActionKind.DELETE_ROUTELINE,
          payload: { id: 0.1 },
        });
      } else if (props.routeState.linePoints.length < 3) {
        alert(
          "Route line has only two points, so complete line will be deleted!"
        );
        props.routeDispatch({
          type: RouteActionKind.DELETE_ROUTELINE,
          payload: { id: 0.1 },
        });
      } else {
        let str: string = props.stateSelected.substring(5);
        props.routeDispatch({
          type: RouteActionKind.DELETE_ROUTELINEPOINT,
          payload: { id: Number(str) },
        });
      }
    }
    props.setSelected("rprop");
    closeDialogBoxDelete();
  }

  // pre:   called when clicked on cancel
  // post:  dialog box is removed from screen
  //        nothing is deleted
  function handleDialogResetDelete(e: any): void {
    closeDialogBoxDelete();
  }

  // pre:   feature name to delete is in stateSelected
  // post:  if selected returns feature name or id
  //        if not returns empty string
  function GetFeatureName(): string {
    if (props.stateSelected.substring(0, 3) === "fss") {
      if (props.stateSelected.length > 3) {
        let str: string = props.stateSelected.substring(3);
        let no: number = GetIndexInArray(
          Number(str),
          props.routeState.focusedSounds
        );
        return props.routeState.focusedSounds[no].name;
      }
    } else if (props.stateSelected.substring(0, 3) === "bss") {
      if (props.stateSelected.length > 3) {
        let str: string = props.stateSelected.substring(3);
        let no: number = GetIndexInArray(
          Number(str),
          props.routeState.backgroundSounds
        );
        return props.routeState.backgroundSounds[no].name;
      }
    } else if (props.stateSelected.substring(0, 4) === "pois") {
      if (props.stateSelected.length > 4) {
        let str: string = props.stateSelected.substring(4);
        let no: number = GetIndexInArray(
          Number(str),
          props.routeState.pointsOfInterest
        );
        return String(props.routeState.pointsOfInterest[no].id);
      }
    } else if (props.stateSelected.substring(0, 5) === "rline") {
      if (props.stateSelected.length === 5) {
        return "Complete Route Line";
      } else {
        let str: string = props.stateSelected.substring(5);
        let no: number = Number(str);
        return "Route Line Point #" + no;
      }
    }
    return "";
  }

  return (
    <section className={styles.container}>
      <Treepanel
        routeState={props.routeState}
        stateSelected={props.stateSelected}
        setSelected={props.setSelected}
        currentLanguage={props.currentLanguage}
      />
      <Mappanel
        className={styles.mapPanel}
        stateSelected={props.stateSelected}
        setSelected={props.setSelected}
        createNewFeature={createNewFeature}
        openDialogBoxDelete={openDialogBoxDelete}
        currentTool={props.currentTool}
        routeState={props.routeState}
        routeDispatch={props.routeDispatch}
        currentLanguage={props.currentLanguage}
        startLocation={startLocation}
        setStartLocation={setStartLocation}
      />
      <Proppanel
        select={props.stateSelected}
        routeState={props.routeState}
        routeDispatch={props.routeDispatch}
        currentLanguage={props.currentLanguage}
      />
      <div className={styles.dialog}>
        {stateIsDialogBoxOpenDelete && (
          <>
            <ReactDialogBox
              closeBox={closeDialogBoxDelete}
              modalWidth="40%"
              headerBackgroundColor="#463278"
              headerTextColor="#f3effb"
              headerHeight="50px"
              closeButtonColor="#f3effb"
              bodyBackgroundColor="#f3effb"
              bodyTextColor="black"
              bodyHeight="80px"
              headerText="Delete Feature"
            >
              <div className={styles.dialogContents}>
                <form
                  method="post"
                  onSubmit={handleDialogSubmitDelete}
                  onReset={handleDialogResetDelete}
                >
                  <label>
                    Are you sure you want do delete feature:{" "}
                    <b>{GetFeatureName()}</b>
                  </label>
                  <hr />
                  <button type="reset">No</button>
                  <button type="submit">Yes</button>
                </form>
              </div>
            </ReactDialogBox>
          </>
        )}
      </div>
    </section>
  );
};

// pre:   - targetId has id of a member, arr is array to look into
// post:  - returns index if member with id found, otherwise -1
export function GetIndexInArray(
  targetId: number,
  arr: POI[] | FocusedSound[] | BackgroundSound[]
): number {
  for (let i: number = 0; i < arr.length; ++i) {
    if (arr[i].id === targetId) return i;
  }
  return -1;
}
